/**
 * usePortalUser.js
 * Custom hook that acquires a Graph API token silently and fetches:
 *   - /me                (display name, email, job title)
 *   - /me/memberOf       (security group IDs for role resolution)
 *
 * Returns: { user, role, authorizedApps, loading, error }
 */

import { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { graphConfig } from "../authConfig";
import { resolveRole, getAuthorizedApps } from "../appRegistry";

const GRAPH_SCOPES = ["User.Read", "GroupMember.Read.All"];

async function callGraph(endpoint, accessToken) {
  const response = await fetch(endpoint, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) throw new Error(`Graph API error: ${response.status} ${endpoint}`);
  return response.json();
}

export function usePortalUser() {
  const { instance, accounts } = useMsal();
  const [state, setState] = useState({
    user:           null,
    role:           null,
    authorizedApps: [],
    loading:        true,
    error:          null,
  });

  useEffect(() => {
    if (!accounts.length) {
      setState(s => ({ ...s, loading: false }));
      return;
    }

    async function fetchUserData() {
      try {
        // Acquire Graph token silently (uses cached token if valid)
        const tokenResponse = await instance.acquireTokenSilent({
          scopes:  GRAPH_SCOPES,
          account: accounts[0],
        });

        const token = tokenResponse.accessToken;

        // Fetch user profile and group memberships in parallel
        const [me, memberOf] = await Promise.all([
          callGraph(graphConfig.graphMeEndpoint, token),
          callGraph(graphConfig.graphGroupsEndpoint, token),
        ]);

        // Extract group Object IDs from the memberOf response
        const groupIds = (memberOf.value ?? [])
          .filter(g => g["@odata.type"] === "#microsoft.graph.group")
          .map(g => g.id);

        const role           = resolveRole(groupIds);
        const authorizedApps = getAuthorizedApps(role);

        setState({
          user: {
            name:     me.displayName,
            email:    me.mail ?? me.userPrincipalName,
            jobTitle: me.jobTitle,
            initials: getInitials(me.displayName),
          },
          role,
          authorizedApps,
          loading: false,
          error:   null,
        });
      } catch (err) {
        console.error("[Portal] Failed to fetch user data:", err);
        setState(s => ({ ...s, loading: false, error: err.message }));
      }
    }

    fetchUserData();
  }, [instance, accounts]);

  return state;
}

function getInitials(displayName = "") {
  return displayName
    .split(" ")
    .slice(0, 2)
    .map(n => n[0]?.toUpperCase() ?? "")
    .join("");
}
